import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import type { IpointTrendDateBasis } from '../../../types/api.js';
import { BAR_MAX_SIZE, BAR_RADIUS_TOP, createCompactLegendContent } from './chart-styles.js';

interface IpointMonthlyTrendChartProps {
  /** i-point月次推移データ */
  data: { month: string; totalIpoint: number; bugCount: number }[];
  /** ローディング状態 */
  isLoading: boolean;
  /** 選択中の表示月数 */
  selectedMonths: string;
  /** 表示月数変更コールバック */
  onMonthsChange: (months: string) => void;
  /** 選択中の集計基準日（起因案件リリース日 / バグ起票日） */
  selectedDateBasis: IpointTrendDateBasis;
  /** 集計基準日変更コールバック */
  onDateBasisChange: (dateBasis: IpointTrendDateBasis) => void;
  /** 月クリック時のコールバック（YYYY-MM 形式の月文字列を受け取る） */
  onMonthClick?: (month: string) => void;
}

/** @graph-connects none */
const MONTHS_OPTIONS = [
  { value: '6', label: '6ヶ月' },
  { value: '12', label: '12ヶ月' },
  { value: '18', label: '18ヶ月' },
  { value: '24', label: '24ヶ月' },
];

/**
 * 集計基準日セレクトの値を IpointTrendDateBasis に絞り込む
 * @graph-connects none
 */
function isIpointTrendDateBasis(value: string): value is IpointTrendDateBasis {
  return value === 'releasedAt' || value === 'createdAt';
}

/**
 * i-point 月次推移チャートカード
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business i-point（エンジニア過失度スコア）の月次累計・評価済みバグ件数・1件あたり平均i-pointの3系列を複合グラフで表示。起因案件リリース日ベース（後からバグが増えると過去月も変わる）とバグ起票日ベース（過去月が確定値）を切り替えて集計し、件数起因か1件あたりの過失度起因かを月単位で判別する
 * @graph-connects Card [calls] カードコンポーネント描画
 * @graph-connects CardHeader [calls] カードヘッダー描画
 * @graph-connects CardContent [calls] カードコンテンツ描画
 * @graph-connects Select [calls] 表示月数・集計基準日セレクト描画
 */
export function IpointMonthlyTrendChart({
  data,
  isLoading,
  selectedMonths,
  onMonthsChange,
  selectedDateBasis,
  onDateBasisChange,
  onMonthClick,
}: IpointMonthlyTrendChartProps) {
  const { t } = useTranslation();

  const dateBasisOptions = [
    { value: 'releasedAt', label: t('kpi.ipointDateBasis.releasedAt') },
    { value: 'createdAt', label: t('kpi.ipointDateBasis.createdAt') },
  ];

  const handleChartClick = onMonthClick
    ? (state: { activeLabel?: string | number }) => {
        if (state.activeLabel != null && typeof state.activeLabel === 'string') {
          onMonthClick(state.activeLabel);
        }
      }
    : undefined;

  const chartData = useMemo(
    () =>
      data.map((d) => ({
        ...d,
        averageIpoint: d.bugCount > 0 ? Math.round((d.totalIpoint / d.bugCount) * 10) / 10 : 0,
      })),
    [data],
  );

  return (
    <Card className="animate-content-reveal">
      <CardHeader
        title={t('kpi.ipointMonthlyTrend')}
        description={
          selectedDateBasis === 'createdAt'
            ? t('kpi.ipointTrendDescriptionCreatedAt')
            : t('kpi.ipointTrendDescription')
        }
      />
      <CardContent>
        <div className="flex justify-end gap-2 mb-3">
          <Select
            options={dateBasisOptions}
            value={selectedDateBasis}
            onChange={(e) => {
              if (isIpointTrendDateBasis(e.target.value)) onDateBasisChange(e.target.value);
            }}
            size="sm"
          />
          <Select
            options={MONTHS_OPTIONS}
            value={selectedMonths}
            onChange={(e) => {
              onMonthsChange(e.target.value);
            }}
            size="sm"
          />
        </div>
        {isLoading ? (
          <div className="h-56 rounded-lg bg-[var(--color-bg-elevated)] animate-pulse" />
        ) : data.length > 0 ? (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{ top: 5, right: 40, left: 10, bottom: 5 }}
                onClick={handleChartClick as never}
                style={onMonthClick ? { cursor: 'pointer' } : undefined}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="month"
                  stroke="var(--color-text-muted)"
                  fontSize={11}
                  tickFormatter={(value: string) => {
                    const [year, month] = value.split('-');
                    return `${String(Number(year) % 100)}/${String(Number(month))}`;
                  }}
                />
                <YAxis
                  yAxisId="left"
                  stroke="var(--color-text-muted)"
                  fontSize={11}
                  tickFormatter={(value: number) => String(value)}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="var(--color-text-muted)"
                  fontSize={11}
                  tickFormatter={(value: number) => String(value)}
                />
                <Tooltip
                  wrapperStyle={{ zIndex: 50, outline: 'none' }}
                  contentStyle={{
                    backgroundColor: 'var(--color-bg-elevated)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    boxShadow: 'var(--shadow-lg)',
                  }}
                  labelStyle={{ color: 'var(--color-text-primary)' }}
                  formatter={
                    ((value: number, name: string) => {
                      if (name === 'totalIpoint') {
                        return [value.toLocaleString(), t('kpi.totalIpoint')];
                      }
                      if (name === 'averageIpoint') {
                        return [value.toFixed(1), t('kpi.averageIpoint')];
                      }
                      return [value.toLocaleString(), t('kpi.bugCount')];
                    }) as never
                  }
                />
                <Legend
                  content={createCompactLegendContent((value) => {
                    if (value === 'totalIpoint') return t('kpi.totalIpoint');
                    if (value === 'averageIpoint') return t('kpi.averageIpoint');
                    return t('kpi.bugCount');
                  })}
                />
                <Bar
                  yAxisId="left"
                  dataKey="totalIpoint"
                  name="totalIpoint"
                  fill="var(--color-warning)"
                  fillOpacity={0.7}
                  stroke="var(--color-warning)"
                  strokeWidth={1}
                  maxBarSize={BAR_MAX_SIZE}
                  radius={BAR_RADIUS_TOP}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="bugCount"
                  name="bugCount"
                  stroke="var(--color-error)"
                  strokeWidth={2}
                  dot={{ r: 3, fill: 'var(--color-error)' }}
                  activeDot={{ r: 5, fill: 'var(--color-error)' }}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="averageIpoint"
                  name="averageIpoint"
                  stroke="var(--color-info)"
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  dot={{ r: 3, fill: 'var(--color-info)' }}
                  activeDot={{ r: 5, fill: 'var(--color-info)' }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-56 flex items-center justify-center border border-dashed border-[var(--color-border)] rounded-lg">
            <p className="text-[var(--color-text-muted)]">{t('status.noData')}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
